import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import DownloadForOfflineOutlinedIcon from '@mui/icons-material/Download'
import ErrorIcon from '@mui/icons-material/Error'
import Box from '@mui/material/Box'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import ButtonMobileComponent from 'components/button-mobile'
import CheckboxComponent from 'components/checkbox'
import { BackIcon, SendIcon } from 'components/icons'
import NavComponent from 'components/nav'
import QuickDataEntry from 'components/quick-data-entry'
import useDeviceSize, { DeviceSize } from 'hooks/useDeviceSize'
import { isPossiblePhoneNumber, parsePhoneNumber } from 'libphonenumber-js/max'
import { useEffect, useState } from 'react'
import { UpsertStudentOutputProps, UpsertStudentProps } from 'types/StudentTypes'
import { baseURL, defaultPhoneNumberCountry, MAX_ALLOWED_ENROLLMENT_INVITES_PER_BATCH } from 'utils/constants'
import SettingsBox from './SettingsBox'

interface ContactEntry {
  email: string
  phone: string
  isValidEmail: boolean
  isValidPhone: boolean
  rawPhone?: string // Store the original phone format
  formattedPhone?: string // Store the formatted phone number
  status?: string // Status message to display
  errorMessage?: string // Error message if invitation failed
  studentId?: string
}

interface BulkContactImportProps {
  onSend: (body: UpsertStudentProps[], loadingMsg?: string, cb?: () => void) => Promise<void>
}

const BulkContactImport = ({ onSend }: BulkContactImportProps): JSX.Element => {
  const deviceSize = useDeviceSize()
  const isMobileView = deviceSize === DeviceSize.xs || deviceSize === DeviceSize.sm

  const [inputText, setInputText] = useState<string>('')
  const [contacts, setContacts] = useState<ContactEntry[]>([])
  const [isValidated, setIsValidated] = useState<boolean>(false)
  const [isConsent, setIsConsent] = useState<boolean>(false)
  const [showResults, setShowResults] = useState<boolean>(false)

  // Reset consent when input text changes
  useEffect(() => {
    if (inputText.trim() !== '') setIsConsent(false)
  }, [inputText])

  // Email validation regex
  const validateEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  }

  // Phone validation using libphonenumber-js
  const validatePhone = (phone: string): boolean => {
    try {
      // First check if it's a possible phone number
      if (isPossiblePhoneNumber(phone, defaultPhoneNumberCountry)) {
        // Check if it has an extension
        const parsedNumber = parsePhoneNumber(phone, defaultPhoneNumberCountry)
        // Return false if it has an extension
        if (parsedNumber.ext) {
          return false
        }
        // Otherwise use the standard validation
        return true
      }
      return false
    } catch (error) {
      return false
    }
  }

  // Normalize phone number for storage while keeping original format
  const normalizePhone = (phone: string): { normalized: string; raw: string; formatted: string } => {
    const raw = phone.trim()
    let normalized = raw
    let formatted = raw

    try {
      // Try to normalize to E.164 format if possible
      if (isPossiblePhoneNumber(raw, defaultPhoneNumberCountry)) {
        // Parse the phone number
        const parsedNumber = parsePhoneNumber(raw, defaultPhoneNumberCountry)
        // Always normalize and format, even if it has an extension
        normalized = parsedNumber.format('E.164')
        formatted = parsedNumber.formatNational()
      } else {
        // For invalid phone numbers, keep the original format
        normalized = raw
      }
    } catch (error) {
      // If parsing fails, keep the original format
      normalized = raw
    }

    return { normalized, raw, formatted }
  }

  // Check if a string looks like a phone number
  const looksLikePhone = (str: string): boolean => {
    // Count digits
    const digitCount = str.replace(/[^0-9]/g, '').length
    // Check for common phone number patterns
    const hasPhonePattern = /[\d\-+()\s.]{7,}/.test(str)
    return digitCount >= 7 && hasPhonePattern
  }

  // Check if a string looks like an email
  const looksLikeEmail = (str: string): boolean => {
    return str.includes('@')
  }

  const handleValidate = () => {
    if (!inputText.trim()) return

    // Split by newlines first
    const lines = inputText.split(/\n/).filter((line) => line.trim())
    const parsedContacts: ContactEntry[] = []

    lines.forEach((line) => {
      // First check for common separators
      let parts: string[] = []

      // Check for various delimiters in order of preference
      if (line.includes('\t')) {
        // Tab delimiter
        parts = line.split('\t').filter((p) => p.trim())
      } else if (line.includes(',')) {
        // Comma delimiter
        parts = line.split(',').filter((p) => p.trim())
      } else if (line.includes(';')) {
        // Semicolon delimiter
        parts = line.split(';').filter((p) => p.trim())
      } else if (line.includes('|')) {
        // Pipe delimiter - sometimes used in data exports
        parts = line.split('|').filter((p) => p.trim())
      } else {
        // Try to intelligently parse the line
        const emailMatch = line.match(/[^\s@]+@[^\s@]+\.[^\s@]+/)

        if (emailMatch) {
          const email = emailMatch[0]
          // Get everything before and after the email
          const beforeEmail = line.substring(0, line.indexOf(email)).trim()
          const afterEmail = line.substring(line.indexOf(email) + email.length).trim()

          // Determine which part is likely the phone number
          if (beforeEmail && afterEmail) {
            // If we have text both before and after email, use the one that looks more like a phone
            const beforeDigits = beforeEmail.replace(/[^0-9]/g, '').length
            const afterDigits = afterEmail.replace(/[^0-9]/g, '').length

            parts = beforeDigits > afterDigits ? [beforeEmail, email] : [email, afterEmail]
          } else {
            // Otherwise use whichever part exists
            parts = beforeEmail ? [beforeEmail, email] : [email, afterEmail]
          }
        } else {
          // No email found, just split by spaces
          parts = line.split(/\s+/).filter((p) => p.trim())
        }
      }

      if (parts.length >= 2) {
        // Try to identify which part is the email and which is the phone
        let email = ''
        let phone = ''
        let rawPhone = ''
        let formattedPhone = ''

        // Determine which part is email and which is phone
        const firstPartIsEmail = looksLikeEmail(parts[0])
        const secondPartIsEmail = looksLikeEmail(parts[1])
        const firstPartIsPhone = looksLikePhone(parts[0])
        const secondPartIsPhone = looksLikePhone(parts[1])

        if (firstPartIsEmail && secondPartIsPhone) {
          // First part is email, second is phone
          email = parts[0].trim()
          const { normalized, raw, formatted } = normalizePhone(parts[1])
          phone = normalized
          rawPhone = raw
          formattedPhone = formatted
        } else if (secondPartIsEmail && firstPartIsPhone) {
          // First part is phone, second is email
          email = parts[1].trim()
          const { normalized, raw, formatted } = normalizePhone(parts[0])
          phone = normalized
          rawPhone = raw
          formattedPhone = formatted
        } else if (firstPartIsEmail) {
          // First part is email, assume second is phone
          email = parts[0].trim()
          const { normalized, raw, formatted } = normalizePhone(parts[1])
          phone = normalized
          rawPhone = raw
          formattedPhone = formatted
        } else if (secondPartIsEmail) {
          // Second part is email, assume first is phone
          email = parts[1].trim()
          const { normalized, raw, formatted } = normalizePhone(parts[0])
          phone = normalized
          rawPhone = raw
          formattedPhone = formatted
        } else {
          // No clear email, use heuristics
          // If first part has more digits, it's likely the phone
          const firstDigits = parts[0].replace(/[^0-9]/g, '').length
          const secondDigits = parts[1].replace(/[^0-9]/g, '').length

          if (firstDigits > secondDigits) {
            const { normalized, raw, formatted } = normalizePhone(parts[0])
            phone = normalized
            rawPhone = raw
            formattedPhone = formatted
            email = parts[1].trim()
          } else {
            const { normalized, raw, formatted } = normalizePhone(parts[1])
            phone = normalized
            rawPhone = raw
            formattedPhone = formatted
            email = parts[0].trim()
          }
        }

        parsedContacts.push({
          email,
          phone,
          rawPhone,
          formattedPhone,
          isValidEmail: validateEmail(email),
          isValidPhone: validatePhone(rawPhone), // Use rawPhone to preserve extension information
        })
      } else if (parts.length === 1 && parts[0].trim()) {
        // Handle case where only one value is provided
        const value = parts[0].trim()
        if (looksLikeEmail(value)) {
          // It's an email
          parsedContacts.push({
            email: value,
            phone: '',
            isValidEmail: validateEmail(value),
            isValidPhone: false,
          })
        } else if (looksLikePhone(value)) {
          // It's a phone
          const { normalized, raw, formatted } = normalizePhone(value)
          parsedContacts.push({
            email: '',
            phone: normalized,
            rawPhone: raw,
            formattedPhone: formatted,
            isValidEmail: false,
            isValidPhone: validatePhone(raw), // Use raw to preserve extension information
          })
        } else {
          // Not sure what it is, assume email
          parsedContacts.push({
            email: value,
            phone: '',
            isValidEmail: validateEmail(value),
            isValidPhone: false,
          })
        }
      }
    })

    // Set contacts and mark as validated
    setContacts(parsedContacts)
    setIsValidated(true)
  }

  const handleBack = () => {
    // Only reset validation state, keep the input text
    setIsValidated(false)
    setContacts([])
  }

  const handleSend = async () => {
    // Filter only valid contacts
    const invitations = contacts
      .filter((contact) => contact.isValidEmail && contact.isValidPhone)
      .map((c) => ({
        userPhoneNumber: c.phone,
        userEmail: c.email,
      }))
    await onSend(invitations, 'Sending Sign Up Invitations...', (data?: UpsertStudentOutputProps) => {
      // Update contacts status to show success/failure
      if (data) {
        // Create copies of the arrays that we can modify as we process contacts
        const remainingCreated = [...data.created]
        const remainingErrored = [...data.errored]

        const updatedContacts = contacts.map((c) => {
          // If it was valid and was sent
          if (c.isValidEmail && c.isValidPhone) {
            // Handle created match
            const createdIndex = remainingCreated.findIndex(
              (u) => u.userPhoneNumber === c.phone && u.userEmail === c.email,
            )
            if (createdIndex !== -1) {
              const createdMatch = remainingCreated[createdIndex]
              // Remove this item so it won't be processed again
              remainingCreated.splice(createdIndex, 1)

              return {
                ...c,
                status: 'Sent invite',
                studentId: createdMatch.studentId,
              }
            }

            // Handle errored match
            const erroredIndex = remainingErrored.findIndex(
              (e) => e.userPhoneNumber === c.phone && e.userEmail === c.email,
            )
            if (erroredIndex !== -1) {
              const erroredMatch = remainingErrored[erroredIndex]
              // Remove this item so it won't be processed again
              remainingErrored.splice(erroredIndex, 1)

              return {
                ...c,
                status: 'Errored invite',
                errorMessage: erroredMatch.message || 'Unknown error',
              }
            }

            // No match found
            return {
              ...c,
              status: 'Errored invite',
              errorMessage: 'Unknown error',
            }
          } else {
            return {
              ...c,
              status: 'Invalid - not sent',
            }
          }
        })

        setContacts(updatedContacts)
        setShowResults(true)
      }
    })
  }

  const handleReset = () => {
    // Reset the component to initial state
    setInputText('')
    setContacts([])
    setIsValidated(false)
    setIsConsent(false)
    setShowResults(false)
  }

  const handleDownloadResults = () => {
    // Create CSV content
    let csvContent = 'Email,Phone,Status,Error Message\n'

    contacts.forEach((contact) => {
      const email = contact.email || ''
      const phone = contact.formattedPhone || contact.rawPhone || contact.phone || ''
      const status = contact.status || (contact.isValidEmail && contact.isValidPhone ? 'Valid' : 'Invalid')
      const errorMsg = contact.errorMessage || ''

      // Escape fields that might contain commas
      const escapedEmail = `"${email.replace(/"/g, '""')}"` // Escape double quotes
      const escapedErrorMsg = `"${errorMsg.replace(/"/g, '""')}"` // Escape double quotes

      csvContent += `${escapedEmail},${phone},${status},${escapedErrorMsg}\n`
    })

    // Create a blob and download it
    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'contact_invites_results.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  const handleDownloadValidation = () => {
    // Create CSV content
    let csvContent = 'Email,Phone,Status\n'

    contacts.forEach((contact) => {
      const email = contact.email || ''
      const phone = contact.formattedPhone || contact.rawPhone || contact.phone || ''

      const status = contact.isValidEmail && contact.isValidPhone ? 'Valid' : 'Invalid'

      // Escape fields that might contain commas
      const escapedEmail = `"${email.replace(/"/g, '""')}"` // Escape double quotes

      csvContent += `${escapedEmail},${phone},${status}\n`
    })

    // Create a blob and download it
    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'contact_validation_results.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  // Textarea validation
  const maxCount = MAX_ALLOWED_ENROLLMENT_INVITES_PER_BATCH
  const inputRowCount = inputText.trim() ? inputText.split(/\n/).filter((line) => line.trim()).length : 0
  const isInputTextExceededMaxCount = inputRowCount > maxCount
  const isValidInputText = inputText.trim() && isConsent && !isInputTextExceededMaxCount
  const hasChanges: boolean = !!inputText // hasChanges is always derived

  // Contact validation
  const totalCount = contacts.length
  const validCount = contacts.filter((c) => c.isValidEmail && c.isValidPhone).length
  const invalidCount = totalCount - validCount
  const sentCount = contacts.filter((c) => c.status === 'Sent invite').length
  const errorCount = contacts.filter((c) => c.status === 'Errored invite').length

  const subtitle: string = isInputTextExceededMaxCount // Check for max count exceeded first
    ? `Contact limit (${maxCount}) exceeded. Reduce list to proceed.`
    : showResults // If showing results after sending
      ? `${sentCount} invitations sent successfully${errorCount > 0 ? `, ${errorCount} failed` : ''}.`
      : isValidated // If validated but not yet sent
        ? `${validCount} of ${totalCount} contacts will receive signup instructions with unique access code`
        : 'Invites emailed on your final confirmation.'

  return (
    <SettingsBox title='Invite Families' subtitle={!showResults ? subtitle : undefined}>
      {showResults && (
        <Typography variant='subtitle2' className='mb-4 text-gray-600'>
          <span style={{ color: '#4CAF50', fontWeight: 'bold' }}>{sentCount}</span> invitations sent successfully
          {errorCount > 0 && (
            <>
              ; <span style={{ color: 'error', fontWeight: 'bold' }}>{errorCount}</span> failed to send
            </>
          )}
          {invalidCount > 0 && (
            <>
              ; <span style={{ color: 'error', fontWeight: 'bold' }}>{invalidCount}</span> were invalid and not sent
            </>
          )}
          .
        </Typography>
      )}
      {!isValidated ? (
        <Box>
          <QuickDataEntry
            value={inputText}
            isError={isInputTextExceededMaxCount}
            onChange={setInputText}
            placeholder={
              'alice.smith@example.com, 919-555-1234\n919.555.5678, bob.jones@school.org\ncharlie.brown@work.net +19195559012'
            }
            height='h-[350px]'
          />
          {hasChanges && (
            <>
              <Box
                display='flex'
                flexDirection='row'
                alignItems='center'
                onClick={() => setIsConsent(!isConsent)}
                sx={{
                  justifyContent: 'flex-start',
                  mx: 1,
                  my: 2,
                  cursor: 'pointer',
                }}
              >
                <CheckboxComponent
                  isChecked={isConsent}
                  isRequired={true}
                  onClick={(e) => {
                    e?.stopPropagation() // Prevent double triggering from Box's onClick
                    setIsConsent(!isConsent)
                  }}
                  label={
                    <span>
                      I confirm that I have the authority to share this parent data with Carpool School Inc. This
                      information will be governed by the Carpool School Inc.{' '}
                      <span
                        onClick={(e) => e.stopPropagation()}
                        style={{ color: '#beac48', fontWeight: 'bold', textDecoration: 'underline' }}
                      >
                        <NavComponent
                          href={`${baseURL}/home/index.html#privacy`}
                          text='Privacy Policy'
                          isOpenNewTab={true}
                        />
                      </span>
                      .
                    </span>
                  }
                />
              </Box>
              <Box display='flex' justifyContent='flex-end'>
                <ButtonMobileComponent
                  text='Validate Contacts'
                  onClick={handleValidate}
                  color='bg-custom-yellow'
                  disabled={!isValidInputText}
                  width='auto'
                />
              </Box>
            </>
          )}
        </Box>
      ) : (
        <Box sx={{ p: 0 }}>
          {showResults ? (
            <>
              <TableContainer sx={{ maxHeight: 400 }}>
                <Table stickyHeader size='small'>
                  <TableHead>
                    <TableRow>
                      <TableCell>Email</TableCell>
                      <TableCell>Phone</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Message</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {contacts.map((contact, index) => {
                      // Determine status color
                      let statusColor = 'primary.main' // Default color
                      if (contact.status === 'Sent invite') {
                        statusColor = '#4CAF50' // custom-green from tailwind config
                      } else if (contact.status === 'Errored invite') {
                        statusColor = 'error.main'
                      } else if (contact.status === 'Invalid - not sent') {
                        statusColor = 'error.main'
                      }

                      return (
                        <TableRow key={index}>
                          <TableCell>
                            <Box display='flex' alignItems='center' gap={1}>
                              {contact.email}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box display='flex' alignItems='center' gap={1}>
                              {contact.formattedPhone || contact.rawPhone || contact.phone}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box display='flex' alignItems='center' gap={1}>
                              <Typography color={statusColor} variant='body2'>
                                {contact.status || 'Not processed'}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant='body2' color='text.secondary'>
                              {contact.errorMessage || ''}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box display='flex' justifyContent='space-between' mt={2}>
                <ButtonMobileComponent
                  text=''
                  data-testid='download-link-button'
                  icon={<DownloadForOfflineOutlinedIcon />}
                  iconPosition={'right'}
                  color='bg-white'
                  width='w-[44px]'
                  padx='px-[8px]'
                  className='border border-[#D0D5DD] gap-[0px]'
                  onClick={handleDownloadResults}
                />
                <ButtonMobileComponent text='Done' onClick={handleReset} color='bg-custom-yellow' width='auto' />
              </Box>
            </>
          ) : (
            <>
              <TableContainer sx={{ maxHeight: 400 }}>
                <Table stickyHeader size='small'>
                  <TableHead>
                    <TableRow>
                      <TableCell>Email</TableCell>
                      <TableCell>Phone</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {contacts.map((contact, index) => {
                      const isValid = contact.isValidEmail && contact.isValidPhone
                      return (
                        <TableRow key={index}>
                          <TableCell>
                            <Box display='flex' alignItems='center' gap={1}>
                              {contact.email}
                              {contact.isValidEmail ? (
                                <CheckCircleIcon fontSize='small' color='success' />
                              ) : contact.email ? (
                                <ErrorIcon fontSize='small' color='error' />
                              ) : null}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box display='flex' alignItems='center' gap={1}>
                              {contact.formattedPhone || contact.rawPhone || contact.phone}
                              {contact.isValidPhone ? (
                                <CheckCircleIcon fontSize='small' color='success' />
                              ) : contact.phone ? (
                                <ErrorIcon fontSize='small' color='error' />
                              ) : null}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box display='flex' alignItems='center' gap={1}>
                              <Typography color={isValid ? 'primary.main' : 'error.main'} variant='body2'>
                                {isValid ? 'Ready to send' : 'Invalid contact'}
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box display='flex' flexDirection='column' sx={{ mx: 1, my: 2 }}>
                <span
                  className='text-gray-400 text-sm font-medium py-2'
                  style={{ flex: 1, margin: 0, padding: 0, display: 'block', textAlign: 'left' }}
                >
                  I confirm that I have the authority to share this parent data with Carpool School Inc. This
                  information will be governed by the Carpool School Inc.{' '}
                  <span style={{ color: '#beac48', fontWeight: 'bold', textDecoration: 'underline' }}>
                    <NavComponent
                      href={`${baseURL}/home/index.html#privacy`}
                      text='Privacy Policy'
                      isOpenNewTab={true}
                    />
                  </span>
                  .
                </span>
                <span
                  className='text-gray-600 text-sm'
                  style={{
                    margin: 0,
                    padding: 0,
                    marginTop: 8,
                    display: 'block',
                    textAlign: 'left',
                    fontWeight: 600,
                    color: '#4b5563',
                  }}
                >
                  Contacts that do not sign up will be purged after 30 days.
                </span>
              </Box>

              <Box display='flex' justifyContent='space-between' mt={2}>
                <Box display='flex' gap={2}>
                  <ButtonMobileComponent
                    text=''
                    data-testid='download-link-button'
                    icon={<BackIcon />}
                    iconPosition={'right'}
                    color='bg-white'
                    width='w-[44px]'
                    padx='px-[8px]'
                    className='border border-[#D0D5DD] gap-[0px]'
                    onClick={handleBack}
                  />
                  <ButtonMobileComponent
                    text=''
                    data-testid='download-link-button'
                    icon={<DownloadForOfflineOutlinedIcon />}
                    iconPosition={'right'}
                    color='bg-white'
                    width='w-[44px]'
                    padx='px-[8px]'
                    className='border border-[#D0D5DD] gap-[0px]'
                    onClick={handleDownloadValidation}
                  />
                </Box>
                <ButtonMobileComponent
                  text={isMobileView ? 'Send' : 'Send Invitations Now'}
                  icon={<SendIcon />}
                  iconPosition='right'
                  onClick={handleSend}
                  color='bg-custom-yellow'
                  disabled={validCount === 0}
                  width='auto'
                />
              </Box>
            </>
          )}
        </Box>
      )}
    </SettingsBox>
  )
}

export default BulkContactImport
